/**
 * Pure unwrapping for the two address-lookup endpoints.
 *
 * ⚠️ THE CONTRACT THIS FILE EXISTS TO PIN (card 53566): `api.get` resolves to the
 * PARSED RESPONSE BODY. `web/services/api.js` `handleResponse` ends `return data;`
 * where `data = JSON.parse(text)` — there is NO axios-style `.data` envelope around
 * it. Both endpoints answer `{ success, data: … }`, so the payload sits exactly ONE
 * `.data` deep:
 *
 *   GET /users/address/autocomplete  -> { success: true, data: { predictions: [ … ] } }
 *   GET /users/address/place-details -> { success: true, data: { street, city, state, zipCode, country } }
 *
 * The component previously read `res.data?.data?.predictions`, one level too deep.
 * That resolved to `undefined` on every SUCCESSFUL response, so the prediction list
 * was always empty and the dropdown could never open — against a live, correctly
 * configured Google Places key. The neighbouring bare `catch` is what kept it silent.
 *
 * These live in `web/utils/` rather than inline in the `.jsx` because `jest.config.js`
 * sets `transform: {}` and `moduleFileExtensions: ['js','mjs','json']`: a `.jsx` file
 * can never be imported by a suite, so an inline expression is structurally untestable.
 * Same reasoning as the existing `web/utils/asyncConsultCopy.js`.
 */

/**
 * Predictions from an autocomplete body. Returns `[]` for any shape that is not a
 * genuine array, so a malformed body degrades to "no matches" rather than throwing
 * inside a render path.
 */
export const parseAddressPredictions = (body) =>
  Array.isArray(body?.data?.predictions) ? body.data.predictions : [];

/**
 * The resolved address from a place-details body, or `null` when absent.
 * `null` (never `{}`) so the caller can tell "no detail" from "empty detail".
 */
export const parseAddressDetails = (body) => {
  const details = body?.data;
  return details && typeof details === 'object' && !Array.isArray(details) ? details : null;
};

/**
 * Whether the prediction list should be shown. Kept as a named predicate so the
 * open/closed decision is asserted directly rather than re-derived in the component.
 */
export const shouldOpenPredictionList = (predictions) =>
  Array.isArray(predictions) && predictions.length > 0;
